/*
  This file is part of the project: "Digiziege"
  An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
  GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

  Copyright (c) 2026 Digiziege Team TH-Bingen

  Description:
  Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
  master data management and health tracking.
  Built as a PWA with a local-first approach to ensure data privacy.

  LICENSE INFORMATION:
  This program is free software: you can redistribute it and/or modify it under the terms of the
  GNU Affero General Public License as published by the Free Software Foundation,
  either version 3 of the License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License along with this program.
  If not, see <https://www.gnu.org/licenses/>.
*/

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { groupRepository } from '@/services/repositories/group.repository.js';
import { useGoatStore } from './goat.store.js';
import { storeToRefs } from 'pinia';

/**
 * Group Store
 * Manages group state and provides actions to interact with group data
 */
export const useGroupStore = defineStore('group', () => {
  // State
  const groups = ref([]);
  const loading = ref(false);
  const error = ref(null);

  // Get goat store for cross-store computed properties
  const goatStore = useGoatStore();
  const { goats } = storeToRefs(goatStore);

  // Computed/Getters
  const groupCount = computed(() => groups.value.length);

  /**
   * Groups enriched with goat count
   * This computed property automatically recalculates when groups or goats change
   */
  const groupsWithGoatCount = computed(() =>
    groups.value.map(group => {
      const goatCount = goats.value.filter(goat => goat.groupId === group.id).length;
      return {
        ...group,
        goatCount
      };
    })
  );

  /**
   * Get goat count for a specific group
   * @param {number} groupId - Group ID
   * @returns {number} Number of goats in the group
   */
  const getGoatCountForGroup = computed(() => (groupId) => {
    return goats.value.filter(goat => goat.groupId === groupId).length;
  });

  /**
   * Total goats across all groups
   */
  const totalGoatsInAllGroups = computed(() =>
    goats.value.filter(goat => goat.groupId != null).length
  );

  /**
   * Groups with no goats
   */
  const emptyGroups = computed(() =>
    groupsWithGoatCount.value.filter(group => group.goatCount === 0)
  );

  // Actions
  /**
   * Fetch all groups from the database
   * @returns {Promise<void>}
   */
  async function fetchGroups() {
    loading.value = true;
    error.value = null;

    try {
      groups.value = await groupRepository.findAll();
      console.log(`Fetched ${groups.value.length} groups`);
    } catch (e) {
      error.value = e.message;
      console.error('Failed to fetch groups:', e);
    } finally {
      loading.value = false;
    }
  }

  async function fetchAllGoats() {
    goats.value = await goatRepository.findAll()
    console.log(`Loaded ${goats.value.length} goats`)
  }

  /**
   * Get a single group by ID
   * @param {number} id - Group ID
   * @returns {Promise<import('../models/group.model.js').Group|null>}
   */
  async function getGroupById(id) {
    loading.value = true;
    error.value = null;

    try {
      const group = await groupRepository.findById(id);
      return group;
    } catch (e) {
      error.value = e.message;
      console.error('Failed to fetch group:', e);
      return null;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Add a new group
   * @param {Omit<import('../models/group.model.js').Group, 'id'|'createdAt'|'updatedAt'|'goatCount'>} group
   * @returns {Promise<number|null>} The created group's ID or null on error
   */
  async function addGroup(group) {
    loading.value = true;
    error.value = null;

    try {
      const id = await groupRepository.create(group);
      await fetchGroups(); // Refresh the list
      console.log(`Group created with ID: ${id}`);
      return id;
    } catch (e) {
      error.value = e.message;
      console.error('Failed to add group:', e);
      return null;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Update an existing group
   * @param {number} id - Group ID
   * @param {Partial<import('../models/group.model.js').Group>} group - Updated group data
   * @returns {Promise<boolean>} Success status
   */
  async function updateGroup(id, group) {
    loading.value = true;
    error.value = null;

    try {
      await groupRepository.update(id, group);
      await fetchGroups(); // Refresh the list
      console.log(`Group ${id} updated`);
      return true;
    } catch (e) {
      error.value = e.message;
      console.error('Failed to update group:', e);
      return false;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Delete a group
   * Note: Goats in this group will have their group_id set to NULL (ON DELETE SET NULL)
   * @param {number} id - Group ID
   * @returns {Promise<boolean>} Success status
   */
  async function deleteGroup(id) {
    loading.value = true;
    error.value = null;

    try {
      await groupRepository.delete(id);
      await fetchGroups(); // Refresh the list
      console.log(`Group ${id} deleted`);
      return true;
    } catch (e) {
      error.value = e.message;
      console.error('Failed to delete group:', e);
      return false;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Clear error message
   */
  function clearError() {
    error.value = null;
  }

  return {
    // State
    groups,
    loading,
    error,

    // Getters
    groupCount,
    groupsWithGoatCount,
    getGoatCountForGroup,
    totalGoatsInAllGroups,
    emptyGroups,

    // Actions
    fetchGroups,
    getGroupById,
    addGroup,
    updateGroup,
    deleteGroup,
    clearError
  };
});
